import React, { useRef, useEffect, useState, useCallback } from "react";
import { Resizable as ReResizable } from "re-resizable";
import cx from "classnames";
import { debounce } from "debounce";
import "./resizable.scss";

export const isPercentage = (val) =>
  typeof val === "string" && val.indexOf("%") > -1;

export const percentToPx = (value, comparativeValue) => {
  if (
    (typeof value === "string" && value.indexOf("px") > -1) ||
    value === "auto" ||
    !comparativeValue
  )
    return value;
  const percent = parseInt(value);
  return (percent / 100) * comparativeValue + "px";
};
export const pxToPercent = (value, comparativeValue) => {
  const val = (Math.abs(value) / comparativeValue) * 100;
  if (value < 0) return -1 * val;
  else return Math.round(val);
};
export const getElementDimensions = (element) => {
  const computedStyle = getComputedStyle(element);

  let height = element.clientHeight,
    width = element.clientWidth; // width with padding

  height -=
    parseFloat(computedStyle.paddingTop) +
    parseFloat(computedStyle.paddingBottom);
  width -=
    parseFloat(computedStyle.paddingLeft) +
    parseFloat(computedStyle.paddingRight);

  return {
    width,
    height,
  };
};

function Resizable({
  propKey,
  nodeWidth,
  nodeHeight,
  onChange,
  active,
  selfWidth = "100%",
  selfHeight = "100%",
  preserveAspectRatio = false,
  useDynamicDimensions = false,
  percentSupport = false,
  percentReferenceElement,
  children,
  className,
  defaultSize,
  ...props
}) {
  const resizable = useRef(null);
  const isResizing = useRef(false);
  const editingDimensions = useRef(null);
  const nodeDimensions = useRef(null);
  nodeDimensions.current = { width: nodeWidth, height: nodeHeight };

  /**
   * Using an internal value to ensure the width/height set in the node is converted to px
   * because for some reason the <re-resizable /> library does not work well with percentages.
   */
  const [internalDimensions, setInternalDimensions] = useState({
    width: (useDynamicDimensions ? nodeWidth : selfWidth) || defaultSize.width,
    height:
      (useDynamicDimensions ? nodeHeight : selfHeight) || defaultSize.height,
  });

  const updateInternalDimensionsInPx = useCallback(() => {
    const { width: nodeWidth, height: nodeHeight } = nodeDimensions.current;

    const width = percentToPx(
      nodeWidth,
      resizable.current &&
        getElementDimensions(resizable.current.resizable.parentElement).width,
    );
    const height = percentToPx(
      nodeHeight,
      resizable.current &&
        getElementDimensions(resizable.current.resizable.parentElement).height,
    );

    setInternalDimensions({
      width: useDynamicDimensions ? width : selfWidth,
      height: useDynamicDimensions ? height : selfHeight,
    });
  }, []);

  const updateInternalDimensionsWithOriginal = useCallback(() => {
    const { width: nodeWidth, height: nodeHeight } = nodeDimensions.current;
    setInternalDimensions({
      width: useDynamicDimensions ? nodeWidth : selfWidth,
      height: useDynamicDimensions ? nodeHeight : selfHeight,
    });
  }, []);

  const getUpdatedDimensions = (width, height) => {
    const dom = resizable.current.resizable;
    if (!dom) return;

    const currentWidth = parseInt(editingDimensions.current.width),
      currentHeight = parseInt(editingDimensions.current.height);

    return {
      width: currentWidth + parseInt(width),
      height: currentHeight + parseInt(height),
    };
  };

  useEffect(() => {
    if (!isResizing.current) updateInternalDimensionsWithOriginal();
  }, [nodeWidth, nodeHeight, updateInternalDimensionsWithOriginal]);

  useEffect(() => {
    const listener = debounce(updateInternalDimensionsWithOriginal, 1);
    window.addEventListener("resize", listener);

    return () => {
      window.removeEventListener("resize", listener);
    };
  }, [updateInternalDimensionsWithOriginal]);

  return (
    <ReResizable
      enable={[
        "top",
        "left",
        "bottom",
        "right",
        "topLeft",
        "topRight",
        "bottomLeft",
        "bottomRight",
      ].reduce((acc, key) => {
        acc[key] = !!active;
        return acc;
      }, {})}
      handleClasses={{
        top: "top-handle",
        left: "left-handle",
        bottom: "bottom-handle",
        right: "right-handle",
        topLeft: "topLeft-handle",
        topRight: "topRight-handle",
        bottomLeft: "bottomLeft-handle",
        bottomRight: "bottomRight-handle",
      }}
      className={cx([
        {
          "grigora-resizable": true,
          flex: true,
          [className]: true,
        },
      ])}
      style={{
        display: "inline-block",
      }}
      ref={(ref) => {
        if (ref) {
          resizable.current = ref;
        }
      }}
      size={internalDimensions}
      onResizeStart={(e) => {
        updateInternalDimensionsInPx();
        e.preventDefault();
        e.stopPropagation();
        const dom = resizable.current.resizable;
        if (!dom) return;
        editingDimensions.current = {
          width: dom.getBoundingClientRect().width,
          height: dom.getBoundingClientRect().height,
        };
        isResizing.current = true;
      }}
      onResize={(_, __, ___, d) => {
        const dom = resizable.current.resizable;
        let { width, height } = getUpdatedDimensions(d.width, d.height);

        // Comment below lines if you want percent support
        if (!percentSupport) {
          width = `${width}px`;
          height = `${height}px`;
        } else if (percentReferenceElement) {
          if (isPercentage(nodeWidth))
            width =
              pxToPercent(
                width,
                getElementDimensions(percentReferenceElement).width,
              ) + "%";
          else width = `${width}px`;

          if (isPercentage(nodeHeight))
            height =
              pxToPercent(
                height,
                getElementDimensions(percentReferenceElement).height,
              ) + "%";
          else height = `${height}px`;

          if (
            isPercentage(width) &&
            percentReferenceElement.style.width === "auto"
          ) {
            width = editingDimensions.current.width + d.width + "px";
          }

          if (
            isPercentage(height) &&
            percentReferenceElement.style.height === "auto"
          ) {
            height = editingDimensions.current.height + d.height + "px";
          }
        }

        onChange(width, height);
      }}
      onResizeStop={(_, __, ___, d) => {
        isResizing.current = false;
        if (useDynamicDimensions) {
          updateInternalDimensionsWithOriginal();
        }
      }}
      {...props}
    >
      {children}
      {active && (
        <div className="resizeable-indicators">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
      )}
    </ReResizable>
  );
}

export default Resizable;
